<template>
  <VueSpinner v-if="isLoading" size="20" color="#232756"/>
  <section id="profileData" class="phero">
    <div class="wrap">
      <span class="eyebrow"><span class="dot"></span>My Page</span>
      <h1>Thank you, {{ authUser.displayName }}</h1>
    </div>
  </section>
  <div class="onr-student-page profile-content app-page">
    <div class="onr-student-page profile-menu">
      <template v-for="(item, index) in menuItems" :key="index">
        <div v-if="item.role === 'All' || item.role === loadedUser.role">
          <button class="profile-menu--item" :class="menuSelectedClasses(item)" @click="menuOpen(item)">
            {{ item.label }}
          </button>
        </div>
      </template>
    </div>
    <div v-if="selectedItem.value === 'profile'" class="onr-student-page profile-menu__content">
      <div class="onr-student-page content-container">
        <h2>{{ selectedItem.label }}</h2>
        <button v-if="!authUser.emailVerified" class="btn btn-outline" type="submit"
                @click="sendVerificationEmail()">
          Verify Email
        </button>
      </div>
      <div class="onr-student-page profile-image-container">
        <ImageUploader
            :initial-image-url="profilePhoto || loadedUser.profileImageUrl || loadedUser.profile_image_url || loadedUser.photoURL || loadedUser.photoUrl || ''"
        />
      </div>

      <div class="edit-account">
        <div class="form-card">
          <p class="form-card__title">Basic Information</p>
          <div class="form-grid">
            <div class="field">
              <label class="field__label">First name</label>
              <input v-model="loadedUser.firstName" class="field__input" type="text" name="firstname"
                     :maxlength="maxInputLength" required>
            </div>
            <div class="field">
              <label class="field__label">Last name</label>
              <input v-model="loadedUser.lastName" class="field__input" type="text" name="lastname"
                     :maxlength="maxInputLength" required>
            </div>
            <div class="field">
              <label class="field__label">Email</label>
              <input :value="loadedUser.email" disabled :readonly="true" class="field__input" type="text" name="email"
                     @input="validateEmail" required>
              <span v-if="!isValidEmail" class="field__error">Please enter a valid email address.</span>
            </div>
            <div class="field">
              <label class="field__label">Date of birth</label>
              <VueDatePicker
                  class="field__datepicker"
                  v-model="myDOB"
                  :format="dobFormat"
                  :text-input="textInputOptions"
                  :enable-time-picker="false"
                  :timezone="{ timezone: 'UTC', exactMatch: true, dateInTz: 'UTC' }"
              />
            </div>
            <div class="field">
              <label class="field__label">Gender</label>
              <select v-model="loadedUser.gender" :disabled="true" class="field__input" required>
                <option disabled value="">Please select one</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div class="field">
              <label class="field__label">Phone/WhatsApp</label>
              <PhoneInput ref="phoneInput" v-model="loadedUser.phone" :residence="loadedUser.residence || loadedUser.country"/>
            </div>
          </div>
        </div>

        <div class="form-card">
          <p class="form-card__title">Location</p>
          <div class="form-grid">
            <div class="field">
              <label class="field__label">Nationality</label>
              <select v-model="loadedUser.country" class="field__input" required>
                <option disabled value="">Please select one</option>
                <template v-for="(country, index) in countries" :key="index">
                  <option :value="country.code">{{ country.name }}</option>
                </template>
              </select>
            </div>
            <div class="field">
              <label class="field__label">Residence</label>
              <select v-model="loadedUser.residence" class="field__input" required>
                <option disabled value="">Please select one</option>
                <template v-for="(country, index) in countries" :key="index">
                  <option :value="country.code">{{ country.name }}</option>
                </template>
              </select>
            </div>
            <div class="field">
              <label class="field__label">City</label>
              <input v-model="loadedUser.location" class="field__input" type="text" name="location"
                     :maxlength="maxInputLength" required>
            </div>
          </div>
        </div>

        <div class="form-card">
          <p class="form-card__title">Profile Details</p>
          <div class="form-grid">
            <div class="field">
              <label class="field__label">Occupation</label>
              <input v-model="loadedUser.occupation" class="field__input" type="text" name="occupation"
                     :maxlength="maxInputLength" required>
            </div>
            <div class="field">
              <label class="field__label">Interests</label>
              <input v-model="loadedUser.interests" class="field__input" type="text" name="interests"
                     :maxlength="maxInputLength" required>
            </div>
            <div class="field">
              <label class="field__label">Field of study</label>
              <input v-model="loadedUser.academicFields" class="field__input" type="text" name="academicFields"
                     :maxlength="maxInputLength" required>
            </div>
          </div>
        </div>

        <div class="form-card">
          <p class="form-card__title">Preferences</p>
          <div class="preference-section">
            <span class="field__label">Subjects</span>
            <div class="preference-options">
              <label
                  v-for="(subject, index) in subjects"
                  :key="`profile-subject-${subject.key}-${index}`"
                  class="preference-checkbox"
                  :class="{ 'preference-checkbox--selected': isSubjectSelected(subject.key) }"
              >
                <input
                    type="checkbox"
                    :checked="isSubjectSelected(subject.key)"
                    @change="onSubjectPreferenceChange(subject, $event)"
                >
                <span>{{ subject.label }}</span>
              </label>
            </div>
          </div>

          <div v-if="loadedUser.role === 'Teacher'" class="preference-section">
            <span class="field__label">Student Location Preferences</span>
            <div class="student-countries-div">
              <button
                  type="button"
                  class="student-country-chip"
                  :class="{ 'student-country-chip--selected': isStudentCountryPreferenceSelected(studentCountry.label) }"
                  v-for="(studentCountry, index) in studentCountryPreferences"
                  :key="`profile-student-country-${studentCountry.key}-${index}`"
                  :aria-pressed="isStudentCountryPreferenceSelected(studentCountry.label)"
                  @click="toggleStudentCountryPreference(studentCountry.label)"
              >
                {{ studentCountry.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" type="submit" @click="updateProfile()">
          Save Changes
        </button>
      </div>
    </div>

    <div
        v-if="selectedItem.value === 'availabilities' && (loadedUser.role === 'Student' || loadedUser.role === 'Teacher')"
        class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <p class="availability-help">
        Please select as many available times as possible so we can match you with a suitable class schedule.
      </p>
      <div class="form-card form-card--compact">
        <div class="available-times-summary">
          <span class="available-times-summary__label">Current available times</span>
          <ul v-if="safeParseJSON(loadedUser.availableTimes, []).length" class="available-times-list">
            <li v-for="(item, index) in safeParseJSON(loadedUser.availableTimes, [])" :key="index">
              {{ item }}
            </li>
          </ul>
          <span v-else class="available-times-summary__empty">
            No available times selected yet.
          </span>
        </div>
      </div>
      <v-divider style="margin-top: 20px;margin-bottom: 20px"></v-divider>
      <div v-if="loadedUser.role === 'Teacher'" class="availability-selector">
        <Meetingselector :selected-meetings="safeParseJSON(loadedUser.availableTimes, [])" @getMeetings="handleMeetings"/>
        <!--<p>Teachers no longer need to schedule individual meeting times.</p>-->
      </div>
      <div v-if="loadedUser.role === 'Student'" class="availability-selector">
        <Meetingselector2 :selected-meetings="safeParseJSON(loadedUser.availableTimes, [])" @getMeetings="handleMeetings"/>
      </div>
      <div class="form-actions" v-if="loadedUser.role==='Student'">
        <button class="btn btn-primary" type="submit" @click="changeAvailabilities()">
          Update
        </button>
      </div>
      <div class="form-actions" v-if="loadedUser.role==='Teacher'">
        <button class="btn btn-primary" type="submit" @click="changeAvailabilities()">
          Update
        </button>
      </div>
    </div>

    <div v-if="selectedItem.value === 'class-history'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <div v-if="classHistory.length" class="onr-student-page content-body">
        <Accordion
            v-for="(item, index) in classHistory"
            :key="index"
            :question="formatHistoryDate(item.schedule)"
            :content="formatClassHistory(item)"
        ></Accordion>
      </div>
      <div v-else class="empty-state">
        No class history is available yet.
      </div>
    </div>

    <div v-if="selectedItem.value === 'my-class'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <div v-if="myClass && myClass.subject && partnerInfo">
        <div class="form-card">
          <p class="form-card__title">Class Info</p>
          <div class="class-people">
            <figure class="class-person">
              <img :src="classPhotos.teacher || defaultPhoto" :alt="`${myClass.teacherName} profile photo`">
              <figcaption><span class="info-label">Teacher</span>{{ myClass.teacherName }}</figcaption>
            </figure>
            <figure class="class-person">
              <img :src="classPhotos.student || defaultPhoto" :alt="`${myClass.studentName} profile photo`">
              <figcaption><span class="info-label">Student</span>{{ myClass.studentName }}</figcaption>
            </figure>
          </div>
          <div class="info-grid">
            <span class="info-label">Subject</span>
            <span class="info-value">{{ myClass.subject }}</span>

            <span class="info-label">Teacher Name</span>
            <span class="info-value">{{ myClass.teacherName }}</span>

            <span class="info-label">Student Name</span>
            <span class="info-value">{{ myClass.studentName }}</span>

            <span class="info-label">Meeting Link</span>
            <span class="info-value"><a v-if="isSafeUrl(myClass.link)" :href="myClass.link" target="_blank" rel="noopener noreferrer">{{ myClass.link }}</a><span v-else>{{ myClass.link }}</span></span>

            <span class="info-label">Upcoming Schedule</span>
            <span class="info-value">
             {{ makeSchedule(myClass.schedule) }}
  <span class="tz-badge">{{ getUserTzBadge() }}</span>
            </span>
          </div>
        </div>

        <LiveTranslationEntry :class-id="myClass.cid" :role="loadedUser.role" />

        <div class="form-card">
          <p class="form-card__title">My {{ reverseRole() }} Info</p>
          <div class="info-grid">
            <span class="info-label">Name</span>
            <span class="info-value" v-if="loadedUser.role === 'Teacher'">{{ myClass.studentName }}</span>
            <span class="info-value" v-if="loadedUser.role === 'Student'">{{ myClass.teacherName }}</span>

            <template v-if="loadedUser.role === 'Teacher' && partnerInfo?.dob">
              <span class="info-label">DOB</span>
              <span class="info-value">{{ new Date(partnerInfo.dob).toISOString().split('T')[0] }}</span>
            </template>
            <span class="info-label">Country</span>
            <span class="info-value">{{ getNameByCode(partnerInfo?.country) || '' }}</span>

            <span class="info-label">Residence</span>
            <span class="info-value">{{ getNameByCode(partnerInfo?.residence) || '' }}</span>

            <span class="info-label">Occupation</span>
            <span class="info-value">{{ partnerInfo?.occupation || '' }}</span>

            <span class="info-label">Interests</span>
            <span class="info-value">{{ partnerInfo?.interests || '' }}</span>

            <span class="info-label">Academic Fields</span>
            <span class="info-value">{{ partnerInfo?.academicFields || '' }}</span>

            <template v-if="loadedUser.role === 'Teacher'">
              <span class="info-label">Email</span>
              <span class="info-value">{{ partnerInfo?.email || '' }}</span>

              <span class="info-label">Phone</span>
              <span class="info-value">{{ partnerInfo?.phone || '' }}</span>
            </template>
          </div>
        </div>

        <div class="form-card">
          <p class="form-card__title">Send a message to your {{ reverseRole() }}</p>
          <div class="form-grid single">
            <div class="field">
              <label class="field__label">To</label>
              <input v-model="contactForm.to" disabled class="field__input" type="text" name="to">
            </div>
            <div class="field">
              <label class="field__label">From</label>
              <input v-model="loadedUser.email" disabled class="field__input" type="text" name="from">
            </div>
            <div class="field">
              <label class="field__label">Message</label>
              <textarea v-model="contactForm.message" class="field__input field__textarea" rows="6"
                        name="message"></textarea>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn btn-primary" @click="sendMessage()">Send Message</button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        You do not have an assigned class yet.
      </div>
    </div>

    <div v-if="selectedItem.value === 'weekly-reports'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <template v-if="myClass && myClass.subject">
      <div class="form-card reports-card">
        <div class="table-scroll">
          <table v-show="true" class="reports-table">
            <thead>
            <tr>
              <th>No</th>
              <!--            <th>Evaluation Report</th>-->
              <th>Submitted Date</th>
              <th>Status</th>
              <th>View</th>
              <th colspan="2">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in evals" :key="index">
              <td>{{ index + 1 }}</td>
              <!--<td @click="reportClick(reports[index])" class="report-name">
                {{ myClass?.subject }} Weekly Report
              </td>-->
              <td>
                <template v-if="reports[index]">
                  {{ formatUtcInUserTz(reports[index].submitted_date) }}
                  <span class="tz-badge">{{ getUserTzBadge() }}</span>
                </template>
                <template v-else>—</template>
              </td>
              <td>
                <span v-if="reports[index] && reports[index].no !== 0" class="status-badge status-badge--success">✔ Submitted</span>
                <span v-else-if="reports[index] && reports[index].no === 0" class="status-badge status-badge--danger">✘ Canceled</span>
                <span v-else class="status-badge status-badge--neutral">Pending</span>
              </td>
              <td>
                <button v-if="reports[index]" @click="fetchReport(reports[index].no)" class="icon-btn">👁</button>
                <span v-else class="icon-btn icon-btn--disabled">👁</span>
              </td>
              <td class="th-actions">
                <button class="btn btn-primary btn-sm" @click="submitReport" v-if="reports.length == index">
                  📝 Submit
                </button>
              </td>
              <td class="th-actions">
                <button class="btn btn-danger btn-sm" @click="cancelClassForm" v-if="reports.length == index">
                  ❌ Cancel
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- View Report -->
      <template v-if="showReport()">
        <div class="form-card" style="margin-top: 1.5rem;">
          <p class="form-card__title">{{ myClass.subject }} Weekly Report #{{ this.reportId }}</p>
          <div class="onr__eval-container--form">
            <div class="field">
              <label class="field__label">Instructor name</label>
              <input v-model="myClass.teacherName" disabled class="field__input" type="text" required>
            </div>
            <div class="field">
              <label class="field__label">Instructor email</label>
              <input v-model="loadedUser.email" disabled class="field__input" type="text" required>
            </div>
            <template v-for="(item, index) in report" :key="index">
              <div class="field">
                <label class="field__label">{{ item.question }}</label>

                <input v-if="item.type === 'short'" v-model="item.response" class="field__input" type="text" readonly>
                <textarea v-if="item.type === 'long'" v-model="item.response" class="field__input field__textarea"
                          rows="4" readonly></textarea>

                <div v-if="item.type === 'radio'" class="radio-group">
                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Yes" disabled/>
                    Yes
                  </label>
                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="No" disabled/>
                    No
                  </label>
                </div>

                <div v-if="item.type === 'mcq'" class="radio-group">
                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Positive" disabled/>
                    Positive
                  </label>

                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Neutral" disabled/>
                    Neutral
                  </label>

                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Needs Improvement"
                           disabled/>
                    Needs Improvement
                  </label>
                </div>
              </div>
            </template>
          </div>
          <div class="form-actions">
            <button class="btn btn-ghost" @click="toggleContent()">Close</button>
          </div>
        </div>
      </template>

      <!-- Submit Report -->
      <template v-if="showMenuId == 1">
        <div ref="submitReportFormTop"></div>
        <div class="form-card" style="margin-top: 1.5rem;">
          <p class="form-card__title">{{ myClass.subject }} Weekly Report #{{ report_no }}</p>
          <div class="onr__eval-container--form">
            <div class="field">
              <label class="field__label">Instructor name</label>
              <input v-model="myClass.teacherName" disabled class="field__input" type="text" required>
            </div>
            <div class="field">
              <label class="field__label">Instructor email</label>
              <input v-model="loadedUser.email" disabled class="field__input" type="text" required>
            </div>
            <template v-for="(item, index) in report_form" :key="index">
              <div class="field">
                <label class="field__label">{{ item.question }}</label>
                <input v-if="item.type === 'short'" v-model="item.response" class="field__input" type="text">
                <textarea v-if="item.type === 'long'" v-model="item.response" class="field__input field__textarea"
                          rows="4"></textarea>
                <div v-if="item.type === 'radio'" class="radio-group">
                  <label class="radio-label"><input type="radio" v-model="item.response" value="Yes"/> Yes</label>
                  <label class="radio-label"><input type="radio" v-model="item.response" value="No"/> No</label>
                </div>
                <div v-if="item.type === 'mcq'" class="radio-group">
                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Positive"/>
                    Positive
                  </label>

                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Neutral"/>
                    Neutral
                  </label>

                  <label class="radio-label">
                    <input type="radio" :name="`q-${index}`" v-model="item.response" value="Needs Improvement"/>
                    Needs Improvement
                  </label>
                </div>
              </div>
            </template>
          </div>
          <div class="form-actions">
            <button class="btn btn-ghost" @click="cancelReport()">Close</button>
            <button class="btn btn-primary" @click="submitEvalForm()">
              {{ isSubmitting ? 'Submitting...' : 'Submit Report' }}
            </button>
          </div>
        </div>
      </template>

      <!-- Cancel Class -->
      <template v-if="showMenuId == 2">
        <div ref="cancelFormTop"></div>
        <div class="form-card form-card--danger" style="margin-top: 1.5rem;">
          <p class="form-card__title">{{ myClass.subject }} Class Cancellation</p>
          <div class="onr__eval-container--form">
            <div class="field">
              <label class="field__label">Instructor name</label>
              <input v-model="myClass.teacherName" disabled class="field__input" type="text" required>
            </div>
            <div class="field">
              <label class="field__label">Instructor email</label>
              <input v-model="loadedUser.email" disabled class="field__input" type="text" required>
            </div>
            <div class="field">
              <label class="field__label">Reason for Cancellation</label>
              <input v-model="cancel_report_form" class="field__input" type="text" required>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn btn-ghost" @click="closeCancelClass()">Close</button>
            <button class="btn btn-danger-filled" @click="cancelSession()">
              {{ isSubmitting ? 'Submitting...' : 'Submit Cancellation' }}
            </button>
          </div>
        </div>
      </template>
      </template>
      <div v-else class="empty-state">
        You do not have an assigned class yet.
      </div>
    </div>

    <div v-if="selectedItem.value === 'meeting-info'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <div class="form-card">
        <p class="form-card__title">Zoom or Google Meet Link</p>
        <div class="field">
          <label class="field__label">Meeting URL</label>
          <v-text-field label="Enter Zoom or Google Meet Link" v-model="zoomLink" outlined></v-text-field>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary" type="submit" @click="updateZoomLink()">Update Link</button>
        </div>
      </div>
      <DreamMeetEntry :current-link="zoomLink" @updated="onDreamMeetLink" />
      <LiveTranslationEntry :class-id="myClass?.cid" :role="loadedUser.role" />
    </div>

    <div v-if="selectedItem.value === 'evaluation'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>
      <div class="form-card">
        <p>Evaluation document located at: </p>
      </div>
    </div>

    <div v-if="selectedItem.value === 'deactivate'" class="onr-student-page profile-menu__content">
      <h2>{{ selectedItem.label }}</h2>

      <div v-if="myStatus === activateStatus" class="status-card">
        <div class="status-card__icon status-card__icon--active">✅</div>
        <div class="status-card__text">
          <h3>Activated</h3>
          <p>If you no longer wish to continue in this program, you can deactivate your account.</p>
        </div>
        <div class="status-card__action">
          <button class="btn btn-danger-filled" @click="updateMyStatus(deactivateStatus)">Deactivate Account</button>
        </div>
      </div>

      <div v-if="myStatus === deactivateStatus" class="status-card">
        <div class="status-card__icon status-card__icon--inactive">⛔</div>
        <div class="status-card__text">
          <h3>Deactivated</h3>
          <p>If you wish to rejoin this program, click to activate your account.</p>
        </div>
        <div class="status-card__action">
          <button class="btn btn-primary" @click="updateMyStatus(activateStatus)">Activate Account</button>
        </div>
      </div>

      <div v-if="myStatus === registeredStatus" class="status-card">
        <div class="status-card__icon status-card__icon--active">🎓</div>
        <div class="status-card__text">
          <h3>Registered</h3>
          <p>You're currently enrolled and taking the course.</p>
        </div>
      </div>
    </div>
  </div>
  <AlertOverlay :showModal="showUpdateOverlay" @close="close"></AlertOverlay>
</template>

<script>
//import {isEmpty} from 'lodash';
// import EditAccountOverlay from '../components/overlays/editAccountOverlay.vue';
import Accordion from '../components/accordion.vue';
import LiveTranslationEntry from '@/components/LiveTranslationEntry.vue';
import DreamMeetEntry from '@/components/DreamMeetEntry.vue';
import {getUser, getVideoLink, updateStudentStatus, updateUser, updateVideoLink} from "@/api";
import {setAvailablities} from "@/api/students";
import {openUploadModal} from "@bytescale/upload-widget-vue";
import ImageUploader from '../components/ImageUploader.vue';
import {findProfileImage} from "@/api/image";
import countries from '@/data/countries.json';
import Meetingselector from "@/components/meetingselector/meetingselector.vue";
import Meetingselector2 from "@/components/meetingselector/meetingselector2.vue";
import PhoneInput from "@/components/PhoneInput.vue";
import {isSafeUrl} from "@/domain/adminDisplay";
import AlertOverlay from '../components/overlays/alertOverlay.vue';
import {getClassHistory, getMyClass} from "@/api/classes";
import {sendMsg} from "@/api/email";
import {auth} from "@/firebase.config";
import {createNullReport, createReport, getReport, getReportQuestions, getReports} from "@/api/reports";
import {VueSpinner,} from 'vue3-spinners';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import {reload, sendEmailVerification} from "firebase/auth";
import {isValidInternationalPhone} from '@/utils/phone';

export default {
  name: 'StudentPage',
  components: {
    LiveTranslationEntry,
    DreamMeetEntry,
    VueDatePicker,
    Accordion,
    ImageUploader,
    Meetingselector,
    Meetingselector2,
    PhoneInput,
    AlertOverlay,
    VueSpinner,
  },
  data() {
    return {
      isLoading: false,
      showMenuId: 0,
      isSubmitting: false,
      textInputOptions: {
        format: 'MM/dd/yyyy'
      },
      dobFormat: 'MM/dd/yyyy',
      showContent: false,
      report: null,
      reportId: 0,
      contactForm: {
        to: '',
        from: '',
        message: '',
      },
      form: [
        {
          question: 'What date/time was your class?',
          type: 'short',
          response: '',
        },
        {
          question: 'How did you run the first class? (eg. Icebreaking, level tests, conversational, any topics talked about, any topics taught etc.)',
          type: 'long',
          response: '',
        },
        {
          question: 'Were there any difficulties holding the class?',
          type: 'long',
          response: '',
        },
        {
          question: 'Were you able to hold the class without help from the local teachers?',
          type: 'radio',
          response: '',
        },
        {
          question: 'Please share any additional details of your student (job, family, hobbies, specialties, dream job, etc.) that could be helpful for the local teachers.',
          type: 'long',
          response: '',
        },
        {
          question: 'If you had a chance to talk about, what is the student\'s motive/purpose/expectations from this program?',
          type: 'long',
          response: '',
        },
        {
          question: 'How was the student\'s attitude/behavior during the class?',
          type: 'long',
          response: '',
        },
        {
          question: 'Was there anything that the student suggested to add, or any topics they wanted to learn?',
          type: 'long',
          response: '',
        },
        {
          question: 'How did you feel about the length of this class (1 hour)?',
          type: 'long',
          response: '',
        },
        {
          question: 'Please share any other feedback or suggestions!',
          type: 'long',
          response: '',
        },
        {
          question: 'Please share any prayer request(s) for this week. We will be praying with you.',
          type: 'long',
          response: '',
        }
      ],
      overlayContent: {
        heading: 'Are you sure you want to update?',
        content: '',
      },
      evals: [
        {
          week: 1,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: 'Oct 1, 2023'
        },
        {
          week: 2,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: 'Oct 17, 2023'
        },
        {
          week: 3,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 4,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 5,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 6,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 7,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 8,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 9,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 10,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 11,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
        {
          week: 12,
          subject: 'SBS',
          season: '8',
          report: 'SBS Season 8 Report Form',
          dueDate: 'Jan 1, 2024',
          dateUploaded: ''
        },
      ],
      maxInputLength: 64,
      deactivateStatus: 4000,
      registeredStatus: 2000,
      activateStatus: 1000,
      showUpdateOverlay: false,
      authUser: {},
      user: {},
      loadedUser: {},
      zoomLink: '',
      inputLink: '',
      myStatus: '',
      myClass: null,
      profilePhoto: '',
      classPhotos: { teacher: '', student: '' },
      defaultPhoto: new URL("../assets/profile.jpeg", import.meta.url).href,
      classHistory: [],
      myDOB: null,
      myAvailabilities: [],
      reports: [],
      report_no: 0,
      report_form: [],
      cancel_report_form: '',
      menuItems: [
        {label: 'Personal Info', value: 'profile', role: 'All', content: {}},
        {label: 'Availabilities', value: 'availabilities', role: 'All', content: {}},
        {label: 'Class History', value: 'class-history', role: 'All', content: []},
        {label: 'Meeting Info', value: 'meeting-info', role: 'Teacher', content: []},
        {label: 'My Class', value: 'my-class', role: 'All', content: [{}]},
        {label: 'Weekly Reports', value: 'weekly-reports', role: 'Teacher', content: [{}]},
        {label: 'My Status', value: 'deactivate', role: 'All', content: {link: 'DEACTIVATE_LINK'}},
      ],
      selectedItem: {
        label: 'Personal Info',
        value: 'profile',
        role: 'All',
        content: {},
      },
      isValidPassword: true,
      isValidEmail: true,
      showEmailError: false,
      showPasswordError: false,
      subjects: [
        {key: 'sbs', label: 'SBS(English)', checked: false},
        {key: 'lbl_coding', label: 'LBL(Coding)', checked: false},
        {key: 'lbl_office', label: 'LBL(MS Office)', checked: false},
        {key: 'han', label: 'Hanbud(Korean)', checked: false}
      ],
      studentCountryPreferences: [
        {key: 'brazil', label: 'Brazil'},
        {key: 'dominican_republic', label: 'Dominican Republic'},
        {key: 'istanbul_gtc', label: 'Istanbul (GTC)'},
        {key: 'istanbul_yak', label: 'Istanbul (YAK)'},
        {key: 'any', label: 'No Preference'},
      ],
      selectedReport: {},
    };
  },
  computed: {
    formattedDate() {
      return new Date(this.originalDate).toISOString().split('T')[0];
    },
    studentData() {
      const studentData = [
        {
          season: '2023',
          students: [
            {
              id: '',
              name: 'Jenny',
              nationality: 'Arabia',
              dob: '10/2/1991',
              interests: 'Interest A, B, C',
              evaluations: [
                {phase: 'Week 1', eval: 'Evaluation_Link'},
                {phase: 'Week 2', eval: 'Evaluation_Link'},
                {phase: 'Week 3', eval: 'Evaluation_Link'}
              ]
            },
          ],
        },
      ];
      return studentData;
    },
    countries() {
      return countries;
    },
    partnerInfo() {
      if (!this.myClass) return null;
      const role = this.reverseRole();
      return this.myClass?.[role] || null;
    },
  },
  async mounted() {
    window.scrollTo({top: 0, behavior: 'smooth'});
    await this.refreshAuthUser();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; // e.g. "Asia/Seoul"
    this.user.timeZone = tz; // 로컬 상태에 보관
    this.loadUserInfo();
  },
  methods: {
    isSafeUrl,
    async refreshAuthUser() {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        this.authUser = JSON.parse(localStorage.getItem('authUser')) || {};
        return;
      }

      try {
        await reload(currentUser);
      } catch (error) {
        console.error(error);
      }

      localStorage.setItem('authUser', JSON.stringify(auth.currentUser));
      this.authUser = JSON.parse(localStorage.getItem('authUser')) || {};
    },
    getUserTimeZone() {
      // 1순위: 서버에서 받은 loadedUser.timeZone (추천)
      // 2순위: 브라우저 자동 감지
      return this.loadedUser?.timeZone
          || this.user?.timeZone
          || Intl.DateTimeFormat().resolvedOptions().timeZone
          || "UTC";
    },

    formatUtcInUserTz(utcDateString, opts = {}) {
      if (!utcDateString) return "";

      const timeZone = this.getUserTimeZone();

      // ✅ "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ" (UTC로 강제)
      const normalized =
          typeof utcDateString === "string" &&
          /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(utcDateString)
              ? utcDateString.replace(" ", "T") + "Z"
              : utcDateString;

      const d = new Date(normalized);
      if (Number.isNaN(d.getTime())) return "";

      return new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        ...opts,
      }).format(d);
    },

    getUserTzBadge() {
      const timeZone = this.getUserTimeZone();
      try {
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone,
          timeZoneName: "short",
        }).formatToParts(new Date());
        return parts.find(p => p.type === "timeZoneName")?.value ?? "";
      } catch {
        return "";
      }
    },
    formatHistoryDate(schedule) {
      return this.formatUtcInUserTz(schedule, {year: "numeric", month: "short", day: "numeric"});
    },

    normalizeClassHistoryResponse(resp) {
      if (Array.isArray(resp)) return resp;
      if (Array.isArray(resp?.classes)) return resp.classes;
      if (Array.isArray(resp?.classHistory)) return resp.classHistory;
      if (Array.isArray(resp?.history)) return resp.history;
      if (Array.isArray(resp?.class)) return resp.class;
      if (resp?.class && typeof resp.class === 'object') return [resp.class];
      if (resp && typeof resp === 'object' && (resp.cid || resp.subject || resp.schedule)) return [resp];
      return [];
    },

    safeParseJSON(str, fallback) {
      try {
        if (str === null || str === undefined) return fallback;
        if (Array.isArray(str)) return str;
        return JSON.parse(str);
      } catch (e) {
        return fallback;
      }
    },
    normalizeArray(value) {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value.trim()) {
        const parsed = this.safeParseJSON(value, value.trim());
        if (typeof parsed === 'string' && parsed.includes(',')) {
          return parsed.split(',').map((item) => item.trim()).filter(Boolean);
        }
        return Array.isArray(parsed) ? parsed : [parsed];
      }
      return [];
    },
    normalizePreferenceToken(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    },
    normalizeStudentCountryPreferenceItem(item) {
      const rawValue = typeof item === 'string'
          ? item
          : item?.label || item?.value || item?.key || item?.name;
      const token = this.normalizePreferenceToken(rawValue);

      if (!token) return '';

      const matchedCountry = this.studentCountryPreferences.find((studentCountry) => {
        return this.normalizePreferenceToken(studentCountry.label) === token
            || this.normalizePreferenceToken(studentCountry.key) === token;
      });

      if (matchedCountry) return matchedCountry.label;

      if (['any', 'none', 'nopreference', 'no'].includes(token)) {
        return 'No Preference';
      }

      return String(rawValue).trim();
    },
    normalizeStudentCountryPreferences(value, useDefault = true) {
      const preferences = [...new Set(
          this.normalizeArray(value)
              .map((item) => this.normalizeStudentCountryPreferenceItem(item))
              .filter(Boolean)
      )];
      return preferences.length || !useDefault ? preferences : ['No Preference'];
    },
    getStudentCountryPreferenceSource(user) {
      if (!user) return undefined;

      const candidates = [
        user.studentCountryPreferences,
        user.studentCountryPreference,
        user.studentLocationPreferences,
        user.studentLocationPreference,
        user.studentCountries,
        user.studentCountry,
        user.preferredStudentCountries,
        user.preferredStudentCountry,
        user.preferredStudentLocations,
        user.preferredStudentLocation,
        user.student_country_preferences,
        user.student_country_preference,
        user.student_location_preferences,
        user.student_location_preference,
      ];

      return candidates.find((value) => this.normalizeStudentCountryPreferences(value, false).length > 0);
    },
    setStudentLocationPreferenceFields(target, preferences) {
      const normalizedPreferences = this.normalizeStudentCountryPreferences(preferences, false);
      const hasSpecificPreference = normalizedPreferences.some((item) => item !== 'No Preference');
      const nextPreferences = [...new Set(
          hasSpecificPreference
              ? normalizedPreferences.filter((item) => item !== 'No Preference')
              : normalizedPreferences
      )];
      const preferenceKeys = [
        'studentCountryPreferences',
        'studentCountryPreference',
        'studentLocationPreferences',
        'studentLocationPreference',
        'preferredStudentCountries',
        'preferredStudentCountry',
        'preferredStudentLocations',
        'preferredStudentLocation',
        'student_country_preferences',
        'student_country_preference',
        'student_location_preferences',
        'student_location_preference',
      ];

      target.studentCountryPreferences = nextPreferences;
      preferenceKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          target[key] = nextPreferences;
        }
      });

      return target;
    },
    removeStudentLocationPreferenceFields(target) {
      const preferenceKeys = [
        'studentCountryPreferences',
        'studentCountryPreference',
        'studentLocationPreferences',
        'studentLocationPreference',
        'studentCountries',
        'studentCountry',
        'preferredStudentCountries',
        'preferredStudentCountry',
        'preferredStudentLocations',
        'preferredStudentLocation',
        'student_country_preferences',
        'student_country_preference',
        'student_location_preferences',
        'student_location_preference',
      ];

      preferenceKeys.forEach((key) => delete target[key]);
      return target;
    },
    normalizeSubjects(value, sourceUser = this.loadedUser) {
      const values = this.normalizeArray(value);
      let keys = values
          .map((item) => typeof item === 'string' ? item : item?.key)
          .filter(Boolean);

      if (!keys.length && sourceUser) {
        keys = this.subjects
            .filter((subject) => Number(sourceUser[subject.key]) === 1 || sourceUser[subject.key] === true)
            .map((subject) => subject.key);
      }

      return this.subjects
          .filter((subject) => keys.includes(subject.key))
          .map((subject) => ({...subject}));
    },
    ensurePreferenceFields() {
      this.loadedUser.subjects = this.normalizeSubjects(this.loadedUser.subjects);
      if (this.loadedUser.role === 'Teacher') {
        this.setStudentLocationPreferenceFields(this.loadedUser, this.normalizeStudentCountryPreferences(
            this.getStudentCountryPreferenceSource(this.loadedUser)
        ));
      } else {
        this.removeStudentLocationPreferenceFields(this.loadedUser);
      }
      this.subjects.forEach((subject) => {
        this.loadedUser[subject.key] = this.loadedUser.subjects.some((item) => item.key === subject.key) ? 1 : 0;
      });
    },
    isSubjectSelected(key) {
      return this.normalizeSubjects(this.loadedUser.subjects).some((subject) => subject.key === key);
    },
    onSubjectPreferenceChange(subject, event) {
      const checked = event.target.checked;
      const subjects = this.normalizeSubjects(this.loadedUser.subjects);

      if (checked && !subjects.some((item) => item.key === subject.key)) {
        this.loadedUser.subjects = [...subjects, {...subject}];
        return;
      }

      if (!checked) {
        this.loadedUser.subjects = subjects.filter((item) => item.key !== subject.key);
      }
    },
    isStudentCountryPreferenceSelected(label) {
      const selectedTokens = this.normalizeStudentCountryPreferences(this.loadedUser.studentCountryPreferences, false)
          .map((item) => this.normalizePreferenceToken(item));
      return selectedTokens.includes(this.normalizePreferenceToken(label));
    },
    toggleStudentCountryPreference(label) {
      const noPreferenceLabel = 'No Preference';
      const currentPreferences = this.normalizeStudentCountryPreferences(this.loadedUser.studentCountryPreferences, false);

      if (currentPreferences.includes(label)) {
        this.setStudentLocationPreferenceFields(
            this.loadedUser,
            currentPreferences.filter((item) => item !== label)
        );
        return;
      }

      if (label === noPreferenceLabel) {
        this.setStudentLocationPreferenceFields(this.loadedUser, [noPreferenceLabel]);
        return;
      }

      this.setStudentLocationPreferenceFields(
          this.loadedUser,
          [...currentPreferences.filter((item) => item !== noPreferenceLabel), label]
      );
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    submitReport() {
      window.scrollBy({top: 180, behavior: 'smooth'});
      this.showMenuId = 1;
      this.scrollToSubmitForm();
    },
    cancelReport() {
      this.showMenuId = 0;
    },
    cancelClassForm() {
      this.showMenuId = 2;
      this.scrollToCancelForm();
    },
    closeCancelClass() {
      this.showMenuId = 0;
    },
    formatClassHistory(item) {
      const formatted = item.schedule
          ? `${this.formatUtcInUserTz(item.schedule)} (${this.getUserTzBadge()})`
          : "";

      return `Subject: ${item.subject}\nTeacher: ${item.teacherName}\nStudent: ${item.studentName}\nSchedule: ${formatted}`;
    },
    async fetchReport(rid) {
      if (rid == null) return;
      this.reportId = rid;
      if (this.reportId === 0) {
        this.showContent = false;
        return;
      }
      this.showContent = true;
      if (this.showContent) {
        getReport(this.myClass.cid, rid).then((resp) => {
          this.report = JSON.parse(resp[0].answer);
        }).catch((error) => {
          console.error(error);
        });
      }
    },
    toggleContent() {
      this.showContent = !this.showContent;
    },
    formatDate(date) {
      return date.toString().slice(0, 21);
    },
    getNameByCode(countryCode) {
      const country = countries.find(country => country.code === countryCode);
      return country ? country.name : null;
    },
    async sendVerificationEmail() {
      if (!auth.currentUser) return;

      sendEmailVerification(auth.currentUser).then(async () => {
        await this.refreshAuthUser();
        alert('An email for verification has been sent.');
      });
    },
    makeSchedule(utcDateString) {
      if (!utcDateString) return "";

      const normalized =
          typeof utcDateString === "string" &&
          /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(utcDateString)
              ? utcDateString.replace(" ", "T") + "Z"
              : utcDateString;

      const start = new Date(normalized);
      if (Number.isNaN(start.getTime())) return "";

      const now = new Date();
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      const dates = Array.from({length: 12}, (_, i) => new Date(start.getTime() + i * oneWeek));

      const future = dates.filter(d => d.getTime() >= now.getTime());
      const pool = future.length ? future : dates;

      const closest = pool.reduce((best, cur) => {
        const diff = Math.abs(cur.getTime() - now.getTime());
        return !best || diff < best.diff ? {date: cur, diff} : best;
      }, null);

      return this.formatUtcInUserTz(closest.date.toISOString());
    },
    // Both people's photos for the My Class card; missing ones fall back to the default avatar.
    loadClassPhotos(myClass) {
      Promise.all([findProfileImage(myClass.tid), findProfileImage(myClass.sid)]).then(([teacher, student]) => {
        this.classPhotos = { teacher: teacher || '', student: student || '' };
      });
    },
    sendMessage() {
      let uid = '';
      if (this.user.role === "Teacher") {
        uid = this.myClass.sid;
      } else if (this.user.role === "Student") {
        uid = this.myClass.tid;
      }
      sendMsg(uid, this.contactForm.message).then(() => {
        this.contactForm.message = '';
        alert('Your message is sent');
      }).catch((error) => {
        console.error(error);
        alert('Error');
      });
    },
    reverseRole() {
      return this.loadedUser.role === 'Student' ? 'teacher' : 'student';
    },
    showForm() {
      return this.report_no != 0 && this.report_no < 9;
    },
    showReport() {
      return this.showContent;
    },
    reportClick(item) {
      this.selectedReport = item;
    },
    async refreshWeeklyReports() {
      // 탭 유지
      this.selectedItem = this.menuItems.find(m => m.value === 'weekly-reports') || this.selectedItem;
      this.showMenuId = 0;
      this.showContent = false;

      if (!this.myClass?.cid) return;

      const resp = await getReports(this.myClass.cid);
      this.reports = resp;

      // 다음 report_no 계산 (기존 로직 그대로)
      let submittedCount = 0;
      for (let i = 0; i < resp.length; i++) {
        if (resp[i].no !== 0) submittedCount += 1;
      }
      this.report_no = submittedCount + 1;

      if (resp.length > 11 || this.report_no > 9) {
        this.report_no = 0;
        return;
      }

      const qs = await getReportQuestions(this.report_no);
      this.report_form = qs;

      // 원하면: Weekly Reports 탭 상단으로 살짝 스크롤
      window.scrollTo({top: 0, behavior: "smooth"});
    },
    submitEvalForm() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      createReport(
          this.myClass.cid,
          this.report_no,
          this.user.uid,
          this.user.firstName + " " + this.user.lastName,
          this.user.email,
          this.report_form,
          this.myClass['student'].uid,
          this.myClass.studentName,
          this.myClass.subject
      ).then(async () => {
        alert('Report submitted');
        await this.refreshWeeklyReports(); // ✅ 상태 유지 + 데이터만 갱신
      }).catch((error) => {
        console.error(error);
      }).finally(() => {
        this.isSubmitting = false;
      });
    },
    cancelSession() {
      createNullReport(this.myClass.cid, this.user.uid, this.user.firstName + " " + this.user.lastName, this.user.email, this.cancel_report_form, this.myClass['student'].uid, this.myClass.studentName, this.myClass.subject).then(async () => {
        alert('Session Canceled');
        await this.refreshWeeklyReports();
      }).catch((error) => {
        console.error(error);
      });
    },
    formUpdated() {
    },
    disableButtonClasses() {
      return this.formUpdated ? '' : 'button--disabled';
    },
    updateProfile() {
      this.$refs.phoneInput?.commitValue();
      if (!this.$refs.phoneInput?.hasSelectedCountry()
          || !isValidInternationalPhone(this.loadedUser.phone)) {
        alert('Please enter a valid Phone/WhatsApp number including the country code.');
        return;
      }
      this.ensurePreferenceFields();
      updateUser(this.loadedUser, this.myDOB).then(() => {
        this.formUpdated = false;
        alert('Profile updated');
      }).catch((error) => {
        console.error(error);
      });
    },
    changeAvailabilities() {
      setAvailablities(this.myAvailablities).then(() => {
        this.loadedUser.availableTimes = JSON.stringify(this.myAvailablities);
        alert('Availabilities Updated');
      }).catch((error) => {
        console.error(error);
      });
    },
    handleMeetings(data) {
      this.myAvailablities = data
    },
    async loadUserInfo() {
      this.isLoading = true;
      return getUser().then((resp) => {
        this.user = resp;
        this.loadedUser = {...resp};
        findProfileImage(auth.currentUser?.uid).then((url) => {
          this.profilePhoto = url || '';
        });
        this.ensurePreferenceFields();
        this.myDOB = new Date(this.user.dob);
        this.myStatus = resp.status
        this.myAvailablities = JSON.parse(this.user.availableTimes)
        getClassHistory(this.user.role).then((resp) => {
          const history = this.normalizeClassHistoryResponse(resp);
          this.classHistory = history.length ? history : (this.myClass ? [this.myClass] : []);
        }).catch((error) => {
          console.error(error);
        });
        getMyClass(this.user.role).then((resp) => {
          if (!resp?.class) {
            this.myClass = null;
            return;
          }

          this.myClass = resp.class;
          this.loadClassPhotos(resp.class);
          if (!this.classHistory.length) {
            this.classHistory = [this.myClass];
          }
          if (this.user.role === "Teacher")
            this.contactForm.to = resp.class.studentName;
          else if (this.user.role === "Student")
            this.contactForm.to = resp.class.teacherName;
          this.myClass['link'] = resp.link ? resp.link.url : 'https://';
          this.myClass['student'] = resp.student;
          this.myClass['teacher'] = resp.teacher;
          if (this.user.role === "Teacher") {
            getReports(this.myClass.cid).then((resp) => {
              this.reports = resp;
              if (resp.length !== 0) {
                this.report_no = 0;
                for (let i = 0; i < resp.length; i++) {
                  if (resp[i].no !== 0) {
                    this.report_no += 1;
                  }
                }
              }
              this.report_no += 1;
              if (resp.length > 11 || this.report_no > 9) {
                this.report_no = 0;
                return;
              }
              getReportQuestions(this.report_no).then((resp) => {
                this.report_form = resp;
              }).catch((error) => {
                console.error(error);
              });
            }).catch((error) => {
              console.error(error);
            });
          }
        }).catch((error) => {
          console.error(error);
        });
        if (this.user.role === 'Teacher') {
          getVideoLink().then((resp) => {
            this.zoomLink = resp.url;
          }).catch((error) => {
            console.error(error);
          });
        }
      }).catch((error) => console.error(error))
          .finally(() => {
            this.isLoading = false;
          });
    },
    formattedAvailabilities(user) {
      const availabilities = JSON.parse(user.availableTimes);
      return availabilities.join(',');
    },
    openUpdateConfirmModal() {
      this.showUpdateOverlay = true;
    },
    close() {
      this.showUpdateOverlay = false;
    },
    uploadFile(event) {
      const options = {maxFileCount: 10};
      openUploadModal({
        event,
        options,
        onComplete: (files) => {
          if (files.length === 0) {
            alert("No files selected.");
          } else {
            alert(files.map(f => f.fileUrl).join("\n"));
          }
        }
      });
    },
    editInfo(val) {
      console.log('..val', val);
    },
    updateMyStatus(status) {
      updateStudentStatus(status).then(() => {
        this.myStatus = status;
        this.myStatus === this.activateStatus ? alert('Account activated') : alert('Account deactivated');
      }).catch((error) => {
        console.error(error);
      });
    },
    studentInfo(item) {
      return `Name: ${item.name}\nNationality: ${item.nationality}\nDOB: ${item.dob}\ninterests: ${item.interests}`;
    },
    menuOpen(item) {
      this.selectedItem = item;
    },
    menuSelectedClasses(item) {
      return this.selectedItem.value === item.value ? 'menu--selected' : '';
    },
    // DreamMeetEntry already saved the link; mirror it in the URL field and My Class.
    onDreamMeetLink(url) {
      this.zoomLink = url;
      if (this.myClass) this.myClass.link = url;
    },
    updateZoomLink() {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)/;
      if (!urlRegex.test(this.zoomLink)) {
        alert('Please enter a valid URL');
        return;
      }
      updateVideoLink(this.zoomLink).then(() => {
        this.myClass.link = this.zoomLink;
        alert('Meeting Info Updated');
      }).catch((error) => {
        console.error(error);
        alert(error?.response?.data?.message || 'Unable to update the meeting link. Please try again.');
      });
    },
    scrollToSubmitForm() {
      this.$nextTick(() => {
        const el = this.$refs.submitReportFormTop?.$el ?? this.$refs.submitReportFormTop;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - 300;
        window.scrollTo({top: y, behavior: 'smooth'});
      });
    },
    scrollToCancelForm() {
      this.$nextTick(() => {
        const el = this.$refs.cancelFormTop?.$el ?? this.$refs.cancelFormTop;
        if (!el) return;

        // "좀만 내려가게" = 상단에서 220px 정도 여유 (값은 취향대로 150~300 추천)
        const y = el.getBoundingClientRect().top + window.pageYOffset - 220;
        window.scrollTo({top: y, behavior: "smooth"});
      });
    },
  },
  watch: {
    user: {
      handler(val) {
        if (!val) return;
        this.loadedUser = {...val};
        this.ensurePreferenceFields();
      }
    },
  },
}
</script>

<style lang="scss">
@use '../utils/_tokens.scss' as *;

/* ─────────────────────────────────────────
   CSS Variables
───────────────────────────────────────── */
:root {
  --blue-1: #{$blue-1};
  --blue-2: #{$blue-2};
  --blue-light: #F4F4F8;
  --border: #E2E3EC;
  --mp-bg: #F4F4F8;
  --text-secondary: #5A5D75;
  --radius: 18px;
  --radius-sm: 12px;
  --mp-shadow: 0 1px 3px rgba(0, 0, 0, .07), 0 4px 16px rgba(0, 0, 0, .05);
}

/* ─────────────────────────────────────────
   HEADER
───────────────────────────────────────── */
.onr-student-page {
  /* ─────────────────────────────────────────
     LAYOUT
  ───────────────────────────────────────── */
  &.profile-content {
    display: flex;
    flex-direction: row;
    min-height: 70vh;
    max-width: var(--wrap);
    margin-inline: auto;
    padding-inline: var(--gut);

    @media only screen and (max-width: 570px) {
      flex-direction: column;
    }
  }

  /* ─────────────────────────────────────────
     SIDEBAR
  ───────────────────────────────────────── */
  &.profile-menu {
    width: 220px;
    flex-shrink: 0;
    background: #fff;
    border-right: 1px solid var(--border);
    padding: 2.5rem 0;

    @media only screen and (max-width: 570px) {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid var(--border);
      padding: .5rem 0;
      display: flex;
      overflow-x: auto;
    }

    .profile-menu--item {
      display: block;
      width: 100%;
      background: none;
      border: none;
      border-left: 3px solid transparent;
      text-align: left;
      padding: .75rem 1.5rem;
      font-family: inherit;
      font-size: .92rem;
      font-weight: 400;
      color: var(--text-secondary);
      cursor: pointer;
      transition: background .15s, color .15s;

      @media only screen and (max-width: 570px) {
        padding: .75rem 1rem;
        border-left: none;
        border-bottom: 3px solid transparent;
        white-space: nowrap;
        flex-shrink: 0;
      }

      &:hover {
        background: var(--blue-light);
        color: $blue-1;
      }

      &.menu--selected {
        background: var(--blue-light);
        color: $blue-1;
        font-weight: 600;
        border-left-color: $gold;

        @media only screen and (max-width: 570px) {
          border-left-color: transparent;
          border-bottom-color: $gold;
        }
      }
    }
  }

  /* ─────────────────────────────────────────
     MAIN CONTENT
  ───────────────────────────────────────── */
  &.profile-menu__content {
    flex: 1;
    padding: 2.5rem 3rem;
    max-width: 860px;
    overflow: hidden;

    @media only screen and (max-width: 570px) {
      padding: 1.5rem;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #23253A;
      margin-bottom: 1.75rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid var(--border);
    }
  }

  &.content-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    h2 {
      margin-bottom: 0;
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &.profile-image-container {
    margin-bottom: 1.5rem;
  }

  &.content-body {
    font-size: 16px;

    .onr-accordion .onr-accordion-slidedown .accordion-content {
      white-space: pre-wrap;
    }
  }
}

/* ─────────────────────────────────────────
   FORM CARD
───────────────────────────────────────── */
.form-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.25rem;
  box-shadow: var(--mp-shadow);

  &__title {
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--text-secondary);
    margin-bottom: 1.25rem;
  }

  &--danger {
    border-left: 4px solid $red-1;
  }

  &--compact {
    padding: 1rem 1.25rem;
  }
}

.empty-state {
  padding: 1.25rem 1.5rem;
  border: 1px dashed var(--border);
  border-radius: var(--radius);
  background: var(--mp-bg);
  color: #5A5D75;
  font-size: .92rem;
}

/* ─────────────────────────────────────────
   FORM GRID
───────────────────────────────────────── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem 1.75rem;

  &.single {
    grid-template-columns: 1fr;
  }

  @media only screen and (max-width: 650px) {
    grid-template-columns: 1fr;
  }
}

/* ─────────────────────────────────────────
   FIELD
───────────────────────────────────────── */
.field {
  display: flex;
  flex-direction: column;
  gap: .35rem;

  &__label {
    font-size: .75rem;
    font-weight: 600;
    color: var(--text-secondary);
    letter-spacing: .05em;
    text-transform: uppercase;
  }

  &__input {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: .55rem .85rem;
    font-family: inherit;
    font-size: .95rem;
    color: #23253A;
    background: var(--mp-bg);
    transition: border-color .15s, box-shadow .15s;
    width: 100%;
    height: 38px;

    &:focus {
      outline: none;
      border-color: $blue-1;
      box-shadow: 0 0 0 3px rgba(59, 70, 184, .18);
      background: #fff;
    }

    &:disabled, &[readonly] {
      background: #F4F4F8;
      color: var(--text-secondary);
      cursor: not-allowed;
    }
  }

  &__textarea {
    height: auto !important;
    resize: vertical;
  }

  &__error {
    font-size: .8rem;
    color: $red-1;
    margin-top: .2rem;
  }

  &__datepicker {
    width: 100% !important;
  }
}

/* DatePicker override */
.field__datepicker {
  :deep(.dp__input) {
    border: 1px solid var(--border) !important;
    border-radius: var(--radius-sm) !important;
    height: 38px !important;
    font-family: inherit !important;
    font-size: .95rem !important;
    background: var(--mp-bg) !important;

    padding: .55rem .85rem !important;
    padding-right: 44px !important; // 아이콘 공간
    box-sizing: border-box !important;

    &:focus {
      border-color: $blue-1 !important;
      box-shadow: 0 0 0 3px rgba(59, 70, 184, .18) !important;
    }
  }

  :deep(.dp__input_icon) {
    right: 12px !important;
  }

  :deep(.dp__input_wrap) {
    margin-right: 0 !important;
  }
}

/* ─────────────────────────────────────────
   FORM ACTIONS (통일된 버튼 정렬)
───────────────────────────────────────── */
.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: .65rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
}

/* ─────────────────────────────────────────
   BUTTON SYSTEM
───────────────────────────────────────── */
// Nested under .tdi .app-page so these variants win over the design's .tdi .btn.
.tdi .app-page .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .4rem;
  padding: .6rem 1.3rem;
  border-radius: 999px;
  font-family: var(--display);
  font-size: .95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s ease;
  border: 1.5px solid transparent;
  line-height: 1.4;
  white-space: nowrap;

  &-sm {
    padding: .35rem .75rem;
    font-size: .8rem;
  }

  &-primary {
    background: $blue-1;
    color: #fff;
    border-color: $blue-1;

    &:hover {
      background: $navy-deep;
      border-color: $navy-deep;
    }
  }

  &-ghost {
    background: transparent;
    color: var(--text-secondary);
    border-color: var(--border);

    &:hover {
      background: var(--mp-bg);
      color: #23253A;
    }
  }

  &-outline {
    background: transparent;
    color: $blue-1;
    border-color: $blue-1;

    &:hover {
      background: var(--blue-light);
    }
  }

  &-danger {
    background: transparent;
    color: $red-1;
    border-color: #FCA5A5;

    &:hover {
      background: #FEF2F2;
    }
  }

  &-danger-filled {
    background: $red-1;
    color: #fff;
    border-color: $red-1;

    &:hover {
      background: $red-2;
      border-color: $red-2;
    }
  }
}

/* ─────────────────────────────────────────
   INFO GRID (My Class)
───────────────────────────────────────── */
.class-people {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.25rem;
}

.class-person {
  display: flex;
  align-items: center;
  gap: .875rem;
  margin: 0;

  img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    background: #F4F4F8;
    border: 1px solid #E2E3EC;
    flex: none;
  }

  figcaption {
    display: flex;
    flex-direction: column;
    gap: .25rem;
    color: #23253A;
    font-weight: 600;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: .75rem 1rem;
  font-size: .95rem;
}

.info-label {
  color: var(--text-secondary);
  font-size: .75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
  align-self: center;
}

.info-value {
  color: #23253A;
  align-self: center;

  a {
    color: $blue-1;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
}

.tz-badge {
  display: inline-block;
  background: var(--blue-light);
  color: $blue-1;
  font-size: .7rem;
  font-weight: 700;
  padding: .1rem .45rem;
  border-radius: 4px;
  margin-left: .4rem;
  letter-spacing: .04em;
}

/* ─────────────────────────────────────────
   REPORTS TABLE
───────────────────────────────────────── */
.reports-card {
  overflow: hidden;
}

.table-scroll {
  overflow-x: visible;
}

// 기본
.reports-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .88rem;

  thead tr {
    background: $blue-1;
    color: #fff;
  }

  th {
    padding: .8rem 1rem;
    text-align: center;
    font-weight: 500;
    font-size: .75rem;
    letter-spacing: .06em;
    text-transform: uppercase;
    border: none;
  }

  td {
    padding: .75rem 1rem;
    text-align: center;
    vertical-align: middle;
    border: none;
    border-bottom: 1px solid var(--border);
    background: #fff;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: var(--blue-light);
  }
}

.report-name {
  text-align: left !important;
  cursor: pointer;
  color: $blue-1;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .2rem .65rem;
  border-radius: 20px;
  font-size: .75rem;
  font-weight: 600;

  &--success {
    background: #DCFCE7;
    color: #15803D;
  }

  &--danger {
    background: #FEE2E2;
    color: #B91C1C;
  }

  &--neutral {
    background: #F1F5F9;
    color: var(--text-secondary);
  }
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  padding: .2rem .4rem;
  border-radius: 4px;
  transition: background .15s;

  &:hover {
    background: var(--blue-light);
  }

  &--disabled {
    opacity: .3;
    cursor: default;
  }
}

/* ─────────────────────────────────────────
   EVAL FORM
───────────────────────────────────────── */
.onr__eval-container--form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  padding: .4rem 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: .4rem;
  font-size: .95rem;
  cursor: pointer;
}

/* ─────────────────────────────────────────
   STATUS CARD (My Status)
───────────────────────────────────────── */
.preference-section {
  display: flex;
  flex-direction: column;
  gap: .65rem;
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.preference-options,
.student-countries-div {
  display: flex;
  flex-wrap: wrap;
  gap: .55rem;
}

.preference-checkbox {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: #fff;
  color: #5A5D75;
  cursor: pointer;
  font-size: .88rem;
  font-weight: 600;
  line-height: 1;
  padding: .62rem .9rem;
  transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease, color .15s ease;

  &:hover {
    border-color: $blue-1;
    color: $blue-1;
    box-shadow: 0 2px 8px rgba(83, 91, 96, .14);
  }

  input {
    accent-color: $blue-1;
  }

  &--selected {
    background: var(--blue-light);
    border-color: $blue-1;
    color: $blue-1;
  }
}

.student-country-chip {
  background-color: #fff;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: .88rem;
  font-weight: 600;
  line-height: 1;
  padding: .65rem .9rem;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease;
  white-space: nowrap;

  &:hover {
    border-color: $blue-1;
    color: $blue-1;
    box-shadow: 0 2px 8px rgba(83, 91, 96, .14);
  }

  &--selected {
    background-color: $blue-1;
    border-color: $blue-1;
    color: #fff;
  }
}
.status-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: #fff;
  box-shadow: var(--mp-shadow);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    flex-shrink: 0;

    &--active {
      background: #DCFCE7;
    }

    &--inactive {
      background: #FEE2E2;
    }
  }

  &__text {
    flex: 1;

    h3 {
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: .2rem;
    }

    p {
      font-size: .875rem;
      color: var(--text-secondary);
    }
  }

  &__action {
    flex-shrink: 0;
  }
}

/* ─────────────────────────────────────────
   AVAILABLE TIMES LIST
───────────────────────────────────────── */
.available-times-list {
  display: flex;
  flex-wrap: wrap;
  gap: .45rem;
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: .28rem .65rem;
    border-radius: 999px;
    background: var(--blue-light);
    color: $blue-1;
    font-size: .82rem;
    font-weight: 600;
    line-height: 1.4;
  }
}

.available-times-summary {
  display: flex;
  align-items: flex-start;
  gap: .85rem;
}

.available-times-summary__label {
  flex: 0 0 auto;
  color: var(--text-secondary);
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .08em;
  line-height: 1.9;
  text-transform: uppercase;
}

.available-times-summary__empty {
  color: var(--text-secondary);
  font-size: .88rem;
  line-height: 1.5;
}
/* ─────────────────────────────────────────
   CONTACT PARTNER (My Class 메시지)
───────────────────────────────────────── */
@media only screen and (max-width: 650px) {
  .available-times-summary {
    flex-direction: column;
    gap: .55rem;
  }
}

.availability-help {
  margin: -1rem 0 1.25rem;
  color: var(--text-secondary);
  font-size: .92rem;
  line-height: 1.55;
}

.availability-selector {
  display: flex;
  justify-content: center;
  width: 100%;
}

.onr__contact-partner-container--form {
  // 기존 스타일 제거 – form-card + form-grid로 대체
}

/* ─────────────────────────────────────────
   MEETING INFO
───────────────────────────────────────── */
.onr-student-page.zoom-link {
  border: 1px solid var(--border);
  font-size: 14px;
  height: 38px;
  padding-left: 12px;
  margin-bottom: 10px;
}

/* ─────────────────────────────────────────
   ACCORDION CONTENT
───────────────────────────────────────── */
.onr-student-page.profile-menu__content {
  .onr-accordion {
    .accordion-content {
      font-size: 15px !important;
      white-space: pre-wrap;
    }
  }
}

/* 테이블 기본은 유지, 모바일에서만 카드형으로 */
@media (max-width: 1280px) {
  .reports-table {
    thead {
      display: none;
    }

    // 헤더 숨기기

    tbody tr {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: .5rem;
      padding: .75rem 1rem;
      border-bottom: 1px solid var(--border);
    }

    td {
      border: none;
      padding: .2rem .4rem;

      // No. 컬럼
      &:nth-child(1) {
        font-weight: 700;
        color: var(--text-secondary);
        font-size: .8rem;
        flex: 0 0 auto;
      }

      // 날짜
      &:nth-child(2) {
        flex: 1 1 auto;
        text-align: left;
        font-size: .85rem;
      }

      // 상태 배지
      &:nth-child(3) {
        flex: 0 0 auto;
      }

      // 👁 보기 버튼
      &:nth-child(4) {
        flex: 0 0 auto;
        margin-left: auto;
      }

      // Submit / Cancel 버튼
      &:nth-child(5),
      &:nth-child(6) {
        flex: 1 1 auto;
      }
    }
  }
}

</style>


